import i18next, { type i18n as I18nInstance } from 'i18next'
import { initReactI18next } from 'react-i18next'
import arCommon from '../locales/ar/common.json'
import enCommon from '../locales/en/common.json'
import { DEFAULT_LOCALE, isLocale, type Locale } from './locales'

const STORAGE_KEY = 'multaqa.locale'

/**
 * Resolve the locale to start in, most specific source first.
 *
 * Read once at startup, before render. Switching afterwards goes through
 * `setLocale()` so the `<html>` attributes stay in step.
 */
export function detectLocale(): Locale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE
  }

  const stored = getStoredLocale()
  if (stored) {
    return stored
  }

  for (const candidate of window.navigator.languages ?? []) {
    const base = candidate.split('-')[0]
    if (base && isLocale(base)) {
      return base
    }
  }

  return DEFAULT_LOCALE
}

/**
 * Read an explicit browser choice without confusing it with the device locale.
 * The distinction matters when an authenticated response supplies a server
 * preference: an explicit choice made on the login screen must win.
 */
export function getStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null

  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored && isLocale(stored) ? stored : null
}

/**
 * Build the shared i18next instance.
 *
 * Call once per application in main.tsx, **before** the first render — a render
 * that happens before init produces a flash of translation keys, and in RTL a
 * flash of the wrong layout direction.
 *
 * Each app passes its own resources on top of the shared `common` namespace, so
 * `admin` and `company` do not have to share a translation file they do not
 * share screens with.
 */
export function createI18n(
  options: { locale?: Locale; resources?: Partial<Record<Locale, Record<string, unknown>>> } = {},
): I18nInstance {
  const locale = options.locale ?? detectLocale()

  void i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    resources: {
      en: { common: enCommon, ...(options.resources?.en ?? {}) },
      ar: { common: arCommon, ...(options.resources?.ar ?? {}) },
    },
    interpolation: {
      // React escapes for us; double-escaping mangles Arabic punctuation.
      escapeValue: false,
    },
    returnNull: false,
  })

  return i18next
}

export function setStoredLocale(locale: Locale): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale)
  }
}

export { i18next }
